import axios from "axios";
import { axiosInstance } from "helpers/interceptor";
import jwt_decode from "jwt-decode";
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState({});
  const [currentUserApi, setCurrentUserApi] = useState({});
  const [currentSub, setCurrentSub] = useState([]);
  const [plan, setPlan] = useState([]);
  // These state variables are maintained for future use or extension
  const [currentPlan, setCurrentPlan] = useState(null); 
  const [counts, setCounts] = useState([]);
  const [userLog, setUserLog] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Axios instance with an interceptor for handling authentication




  useEffect(() => {
    // Check if a user is already authenticated (e.g., by checking a token in local storage)
    setAuthLoading(true);
    fetchPlans();
    const token = retrieveToken();
    
    if (token) {
      // Fetch user details after the AuthProvider initializes
      setIsAuthenticated(true);
      fetchDetails(token);
      fetchApi(token);
      fetchSubscription(token);
      fetchUserLog(token);
    } else {
      setIsAuthenticated(false);
    }
    
    setAuthLoading(false);
  }, []);

  const refreshData = () => {
    const token = retrieveToken();
    if (token) {
      fetchApi(token);
      fetchSubscription(token);
    }
  };

  const login = async (user) => {
    try {
      // Store token before making authenticated requests
      storeToken(user, 12);
      setIsAuthenticated(true);
      
      // Fetch all user data after successful authentication
      fetchPlans();
      fetchDetails(user);
      fetchApi(user);
      fetchSubscription(user);
      fetchUserLog(user);
      
      return true;
    } catch (error) {
      console.error("Error logging in:", error);
      return false;
    }
  };

  const retrieveToken = () => {
    try {
      const token = localStorage.getItem("token");
      const expirationTime = localStorage.getItem("tokenExpiration");

      if (!token || !expirationTime) {
        return null;
      }

      const currentTime = new Date().getTime();
      if (currentTime > parseInt(expirationTime, 10)) {
        localStorage.removeItem("token");
        localStorage.removeItem("tokenExpiration");
        return null;
      }

      return token;
    } catch (error) {
      console.error("Error retrieving token:", error);
      return null;
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axiosInstance.get(`plans/`);
      setPlan(response.data.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const storeToken = (token, expirationInHours) => {
    try {
      const expirationMs = expirationInHours * 60 * 60 * 1000;
      const expirationTime = new Date().getTime() + expirationMs;

      localStorage.setItem("token", token);
      localStorage.setItem("tokenExpiration", expirationTime);
    } catch (error) {
      console.error("Error storing token:", error);
    }
  };

  const fetchDetails = async (token) => {
    try {
      const decodedToken = jwt_decode(token);
      const response = await axiosInstance.get(`user/${decodedToken.id}`);
      setCurrentUser(response.data.data.user);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const fetchApi = async (token) => {
    try {
      const decodedToken = jwt_decode(token);
      const response = await axiosInstance.get(`apikey/${decodedToken.id}`);
      setCurrentUserApi(response.data);
    } catch (error) {
      console.error("Error fetching API details:", error);
    }
  };

  const updateCurrentUser = async (updatedUser) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return false;
      }

      const response = await axiosInstance.put(
        `user/${updatedUser.id}`,
        updatedUser
      );

      if (response.status === 200) {
        setCurrentUser(updatedUser);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error updating user:", error);
      return false;
    }
  };

  const fetchSubscription = async (token) => {
    try {
      const decodedToken = jwt_decode(token);
      const response = await axiosInstance.get(
        `subscriptions/${decodedToken.id}`
      );
      setCurrentSub(response.data.data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };


  const fetchUserLog = async (token) => {
    try {
      const response = await axiosInstance.get(`dashboard/logs-count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserLog(response.data.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const fetchUserLogWithDate = async (token, startDate, endDate) => {
    try {
      const response = await axios.get(`dashboard/logs-count/?start_date=${startDate}&end_date=${endDate}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserLog(response.data.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };


  const logout = () => {
    // Clear user state
    setCurrentUser(null);
    setCurrentUserApi({});
    setCurrentSub([]);
    setIsAuthenticated(false);
    
    // Clear authentication data from storage
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpiration");
  };

  const value = {
    plan,
    currentUser,
    retrieveToken,
    login,
    logout,
    updateCurrentUser,
    currentUserApi,
    currentSub,
    currentPlan,
    axiosInstance,
    refreshData,
    counts,
    userLog,
    setUserLog,
    fetchUserLogWithDate,
    isAuthenticated,
    authLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}